import { useEffect, useState, useRef, useCallback } from "react";
import ProductCard from "../../components/controls/ProductCard";
import ServiceCard from "../../components/controls/ServiceCard";
import CartSidebar from "../EventCreation/Components/CartSidebar";
import { useCart } from "../../contexts/CartContext";
import { useTranslation } from "react-i18next";
import StoreFilters from "./StoreFilters";
import AddToCartModal from "../EventCreation/Components/AddtoCartModal";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import DevelopmentNoticeModal from "./DevelopmentNoticeModal";
import Button from "../../components/controls/Button";
import PageHeader from "../../components/controls/PageHeader";
import AnanasSolutionsLogo from "../../images/AnanasSolutionsLogo.webp";
import { useAuth } from "../../contexts/AuthContext";

const itemsPerPage = 9;

export default function StorePage() {
  const navigate = useNavigate();
  const [activeType, setActiveType] = useState("product");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const sectionRef = useRef(null);
  const [maxPrice, setMaxPrice] = useState(1000);
  const [minPrice, setMinPrice] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [uniqueCompanies, setUniqueCompanies] = useState([]);
  const [showDevNotice, setShowDevNotice] = useState(true);

  const { cart, handleAddToCart, setUserId } = useCart();
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const { currentUser } = useAuth();

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    if (currentUser?.id) setUserId(currentUser.id);
  }, [currentUser, setUserId]);

  useEffect(() => {
    fetchStoreItems();
  }, [
    searchTerm,
    currentPage,
    activeType,
    minPrice,
    maxPrice,
    selectedCompany,
  ]);

  const fetchStoreItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search: searchTerm,
        page: currentPage.toString(),
        pageSize: itemsPerPage.toString(),
      });
      if (minPrice) params.append("minPrice", minPrice.toString());
      if (maxPrice) params.append("maxPrice", maxPrice.toString());

      const res = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/Store/${activeType}s?${params.toString()}`
      );
      const data = await res.json();

      setItems(data.items || []);
      setTotalItems(data.totalCount || 0);
    } catch (error) {
      console.error(t("store.errors.fetchItems"), error);
    } finally {
      setLoading(false);
    }
  }, [
    t,
    searchTerm,
    currentPage,
    activeType,
    minPrice,
    maxPrice,
    selectedCompany,
  ]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const openCart = () => {
    navigate("/cart-store");
  };

  const scrollToTop = () => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center mt-6">
        <div className="join">
          <button
            className="join-item btn"
            disabled={currentPage === 1}
            onClick={() => {
              setCurrentPage((prev) => Math.max(prev - 1, 1));
              scrollToTop();
            }}
            aria-label={t("store.pagination.ariaPrev")}
          >
            {t("store.pagination.prev")}
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={`join-item btn ${
                currentPage === i + 1 ? "btn-active" : ""
              }`}
              onClick={() => {
                setCurrentPage(i + 1);
                scrollToTop();
              }}
            >
              {i + 1}
            </button>
          ))}

          <button
            className="join-item btn"
            disabled={currentPage === totalPages}
            onClick={() => {
              setCurrentPage((prev) => Math.min(prev + 1, totalPages));
              scrollToTop();
            }}
            aria-label={t("store.pagination.ariaNext")}
          >
            {t("store.pagination.next")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div ref={sectionRef} className=" text-primary">
      <PageHeader
        title={t("store.title", "Boutique")}
        subtitle={t(
          "store.subtitle",
          "Produits & services d’entreprises locales"
        )}
        logoSrc={AnanasSolutionsLogo}
        align="center"
        fullBleed={true}
        height="md"
        rounded={false}
        gradient={{ from: "#E9FBFA", via: "#FFF9E6", to: "#FFE9EC" }}
        patternSize="32px 32px"
        patternOpacity={15}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-4">
        {/* Filtres à gauche */}
        <div className="lg:col-span-2">
          <StoreFilters
            activeType={activeType}
            setActiveType={setActiveType}
            searchTerm={searchTerm}
            handleSearchChange={handleSearchChange}
            maxPrice={maxPrice}
            setMaxPrice={setMaxPrice}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            uniqueCompanies={uniqueCompanies}
            setMinPrice={setMinPrice}
          />
        </div>

        {/* Contenu principal */}
        <div className="lg:col-span-7 space-y-4">
          {loading ? (
            <p className="text-center py-10">{t("store.loading")}</p>
          ) : items.length === 0 ? (
            <p className="text-gray-500 italic text-center">
              {t("store.empty")}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((item, index) =>
                  activeType === "product" && item.product ? (
                    <ProductCard
                      key={item.product.id || `p-${index}`}
                      product={item.product}
                      showAddToCartButton
                      onAddToCart={(configured) =>
                        configured &&
                        handleAddToCart({
                          ...configured,
                          productId: configured.id,
                          itemType: "product",
                        })
                      }
                    />
                  ) : item.service ? (
                    <ProductCard
                      key={item.service.id || `s-${index}`}
                      product={item.service}
                      showAddToCartButton
                      onAddToCart={(configured) =>
                        configured &&
                        handleAddToCart({
                          ...configured,
                          serviceId: configured.id,
                          itemType: "service",
                        })
                      }
                      isService={true}
                    />
                  ) : null
                )}
              </div>

              {renderPagination()}
            </>
          )}
        </div>

        {/* Panier à droite */}
        <div className="lg:col-span-3">
          <CartSidebar cart={cart} lang={lang} showBudget={false} />
        </div>
      </div>

      <div className="text-right">
        <Button
          as="button"
          type="button"
          onClick={openCart}
          color="primary"
          size="lg"
          disabled={cart.length === 0}
          rightIcon={<ChevronRightIcon className="w-6 h-6" />}
          className="fixed bottom-8 right-8 z-50 text-lg font-semibold animate-scaleUpDown"
        >
          {t("store.viewCart")}
        </Button>
      </div>

      {showDevNotice && (
        <DevelopmentNoticeModal onClose={() => setShowDevNotice(false)} />
      )}
    </div>
  );
}
